import * as PlaybackStateActions from "actions/PlaybackStateActions";
import * as UserInteractionActions from "actions/UserInteractionActions";
import isBoolean from "lodash/isBoolean";
import ChapterControls from "components/ChapterControls";
import getCanControlVolume from "selectors/device/getCanControlVolume";
import getCanInitiallyAutoplay from "selectors/device/getCanInitiallyAutoplay";
import getDuration from "selectors/chapter/getDuration";
import getTopLevelChapterIndex from "selectors/chapter/getTopLevelChapterIndex";
import getIsProgressBarProgressHidden from "selectors/chapter/getIsProgressBarProgressHidden";
import getIsSubchapter from "selectors/getIsSubchapter";
import getPlaybackState from "selectors/chapter/getPlaybackState";
import getReceivedUserInteraction from "selectors/device/getReceivedUserInteraction";
import getUseInactivity from "selectors/getUseInactivity";
import makeGetChapterEntities from "selectors/chapter/makeGetChapterEntities";
import makeGetShowCuepoints from "selectors/cuepoint/makeGetShowCuepoints";
import { AnalyticsActions } from "utils/Analytics";
import {
  compose,
  onlyUpdateForPropTypes,
  setDisplayName,
  withHandlers,
} from "recompose";
import { connect } from "react-redux";
import { setForceChapterCover } from "actions/ForceChapterCoverActions";
import getBackButtonClickHandler from "selectors/navigation/getBackButtonClickHandler";
import getChapterButtonSize from "selectors/styles/getChapterButtonSize";
import makeGetChapterEntity from "selectors/chapter/makeGetChapterEntity";
import NavigationWrapper from "utils/NavigationWrapper";

const getShowCuepoints = makeGetShowCuepoints();

const connector = connect(
  (state, ownProps) => {
    const stateKey = ownProps.chapterId
      ? `chapter-${ownProps.chapterId}`
      : "story";

    const hideProgressBarProgress = getIsProgressBarProgressHidden(
      state,
      ownProps,
    );
    const isSubchapter = getIsSubchapter(state, ownProps);
    const playbackState = getPlaybackState(state, { stateKey });
    const receivedUserInteraction = getReceivedUserInteraction(state, ownProps);

    const getChapterEntities = makeGetChapterEntities(state, ownProps);

    let effectiveWaitingForInitialPlay = false;
    if (
      !state.playerOptions.autoplay &&
      state.playerOptions.embedded &&
      getCanInitiallyAutoplay(state, ownProps)
    ) {
      effectiveWaitingForInitialPlay =
        !state.userInteraction.receivedUserInteraction;
    }

    const topLevelChapterCount = state.story.topLevelChapters.length;

    return {
      adDuration: state.ads.adDuration,
      border: true,
      canControlVolume: getCanControlVolume(state, ownProps),
      chapterButtonSize: getChapterButtonSize(state, ownProps),
      chapterEntities: getChapterEntities(state, ownProps),
      chapterIndex: getTopLevelChapterIndex(state, ownProps),
      currentChapterKey: state.route.params.chapterKey,
      disableProgressBar:
        state.forceChapterCover || effectiveWaitingForInitialPlay,
      duration: getDuration(state, ownProps),
      financedUnlockedNavigationArrows:
        state.playerOptions.financedUnlockedNavigationArrows,
      hideProgressBarProgress,
      isChapterCoverShowing: state.forceChapterCover,
      isFullscreen: state.controls.isFullscreen,
      onReturnToParentChapterClick: getBackButtonClickHandler(state, ownProps),
      playing: playbackState.playing,
      receivedUserInteraction,
      showAdInterface: false,
      showChapterNumber: !isSubchapter && state.playerOptions.showChapterNumber,
      showChapterNavigationMarkers:
        !isSubchapter &&
        topLevelChapterCount !== 1 &&
        state.playerOptions.chapterNavigationButtons !== "hide",
      showCuepoints: isBoolean(ownProps.showCuepoints)
        ? ownProps.showCuepoints
        : getShowCuepoints(state, ownProps),
      showCurrentChapterMarker: !isSubchapter,
      showCoutdownTimerInControls:
        state.playerOptions.showCoutdownTimerInControls &&
        !hideProgressBarProgress,
      showSliderAboveControls: state.playerOptions.showSliderAboveControls,
      showSeekButtons: state.playerOptions.showSeekButtons,
      showSettingsMenu: state.playerOptions.showSettingsMenu,
      stateKey,
      time: playbackState.time,
      useInactivity: getUseInactivity(state, ownProps),
      waitingForInitialPlay: effectiveWaitingForInitialPlay,
      onChapterNextClick: () => {
        const currentChapterIndex = state.story.topLevelChapters.findIndex(
          ({ id }) => id === ownProps.chapterId,
        );
        if (currentChapterIndex >= state.story.topLevelChapters.length - 1) {
          // Can't go next
          return;
        }
        const nextChapterLink =
          state.story.topLevelChapters[currentChapterIndex + 1];
        const nextChapterEntity = makeGetChapterEntity()(state, {
          chapterId: nextChapterLink.id,
        });

        NavigationWrapper.push(
          "chapter",
          state.route.params.storySlug,
          nextChapterEntity.slugs[0],
          {
            destroyPlaybackStateForChapterId: nextChapterLink.id, // destroy playback state before transitioning to ensure starting at the beginning
          },
        );
      },
      onChapterPreviousClick: () => {
        const currentChapterIndex = state.story.topLevelChapters.findIndex(
          ({ id }) => id === ownProps.chapterId,
        );
        if (currentChapterIndex <= 0) {
          // Can't go back
          return;
        }
        const nextChapterLink =
          state.story.topLevelChapters[currentChapterIndex - 1];
        const nextChapterEntity = makeGetChapterEntity()(state, {
          chapterId: nextChapterLink.id,
        });

        NavigationWrapper.push(
          "chapter",
          state.route.params.storySlug,
          nextChapterEntity.slugs[0],
          {
            destroyPlaybackStateForChapterId: nextChapterLink.id, // destroy playback state before transitioning to ensure starting at the beginning
          },
        );
      },
    };
  },
  (dispatch, ownProps) => ({
    dispatch,
    onPause: () =>
      dispatch(
        PlaybackStateActions.changePlaybackState(ownProps, {
          playing: false,
        }),
      ),
    onSlideProgress: (time) =>
      dispatch(
        PlaybackStateActions.changePlaybackState(ownProps, {
          playing: false,
          scrubbing: true,
          time,
        }),
      ),
    onSlideStart: () =>
      dispatch(
        PlaybackStateActions.changePlaybackState(ownProps, {
          scrubbing: true,
          playing: false,
        }),
      ),
  }),
  (stateProps, dispatchProps, ownProps) => ({
    ...ownProps,
    ...stateProps,
    ...dispatchProps,
    onReturnToParentChapterClick() {
      dispatchProps.onPause();
      stateProps.onReturnToParentChapterClick();
    },
    onSlideStop: (time) =>
      dispatchProps.dispatch(
        PlaybackStateActions.changePlaybackState(ownProps, {
          playing: !stateProps.isChapterCoverShowing,
          scrubbing: false,
          time,
        }),
      ),
    onTogglePlay: () => {
      AnalyticsActions.playOrPause(ownProps);

      if (stateProps.waitingForInitialPlay) {
        dispatchProps.dispatch(
          UserInteractionActions.receivedUserInteraction(),
        );
        dispatchProps.dispatch(setForceChapterCover(false));
        return;
      }

      dispatchProps.dispatch(
        PlaybackStateActions.changePlaybackState(ownProps, {
          playing: !stateProps.playing,
          userInitiatedPause: stateProps.playing,
        }),
      );

      ownProps.onPlayClick && ownProps.onPlayClick();
    },
  }),
);

export default compose(
  setDisplayName("ChapterControlsContainer"),
  connector,
  withHandlers({
    dispatch: (props) => props.dispatch,
    onReturnToParentChapterClick: (props) => props.onReturnToParentChapterClick,
    onSlideProgress: (props) => props.onSlideProgress,
    onSlideStart: (props) => props.onSlideStart,
    onSlideStop: (props) => props.onSlideStop,
    onTogglePlay: (props) => props.onTogglePlay,
  }),
  onlyUpdateForPropTypes,
)(ChapterControls);
